import { useState, useMemo } from 'react'
import { format, addDays } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import { CalendarIcon } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog'
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerFooter,
  DrawerDescription,
} from '@/components/ui/drawer'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Calendar } from '@/components/ui/calendar'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { cn } from '@/lib/utils'
import { useIsMobile } from '@/hooks/use-mobile'
import { useCreateBulkPayments, PaymentInsert } from '@/hooks/usePayments'

interface InstallmentsDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  projectId: string
  projectName: string
  totalValue: number
}

export function InstallmentsDialog({ 
  open, 
  onOpenChange, 
  projectId, 
  projectName,
  totalValue 
}: InstallmentsDialogProps) {
  const isMobile = useIsMobile()
  const [installments, setInstallments] = useState(3)
  const [interval, setInterval] = useState(30)
  const [firstDueDate, setFirstDueDate] = useState(addDays(new Date(), 30))
  
  const createBulkPayments = useCreateBulkPayments()
  
  const installmentsList = useMemo(() => {
    const list = []
    const baseValue = totalValue / installments
    let remaining = totalValue
    
    for (let i = 0; i < installments; i++) {
      const isLast = i === installments - 1
      const value = isLast ? remaining : Math.floor(baseValue * 100) / 100
      remaining -= value
      
      const dueDate = addDays(firstDueDate, i * interval)
      
      list.push({
        number: i + 1,
        value: isLast ? value + remaining : value, // Add any remaining cents to last installment
        dueDate,
      })
    }
    
    return list
  }, [totalValue, installments, interval, firstDueDate])
  
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
      minimumFractionDigits: 2,
    }).format(value)
  }
  
  const handleCreate = async () => {
    const payments: PaymentInsert[] = installmentsList.map((inst) => ({
      project_id: projectId,
      amount: inst.value,
      description: `Parcela ${inst.number}/${installments} - ${projectName}`,
      due_date: format(inst.dueDate, 'yyyy-MM-dd'),
      status: 'pending' as const,
    }))
    
    try {
      await createBulkPayments.mutateAsync(payments)
      onOpenChange(false)
    } catch (error) {
      // Error handled by mutation
    }
  }
  
  const Content = (
    <div className="space-y-6">
      <div className="rounded-lg bg-muted p-4">
        <p className="text-sm text-muted-foreground">Valor total do projeto</p>
        <p className="text-2xl font-bold">{formatCurrency(totalValue)}</p>
      </div>
      
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="installments">Número de parcelas</Label>
          <Input
            id="installments"
            type="number"
            min={1}
            max={24}
            value={installments}
            onChange={(e) => setInstallments(Math.max(1, Math.min(24, parseInt(e.target.value) || 1)))}
          />
        </div>
        
        <div className="space-y-2">
          <Label htmlFor="interval">Intervalo (dias)</Label>
          <Input
            id="interval"
            type="number"
            min={1}
            max={365}
            value={interval}
            onChange={(e) => setInterval(Math.max(1, parseInt(e.target.value) || 30))}
          />
        </div>
      </div>
      
      <div className="space-y-2">
        <Label>Primeira parcela vence em</Label>
        <Popover>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              className="w-full justify-start text-left font-normal"
            >
              <CalendarIcon className="mr-2 h-4 w-4" />
              {format(firstDueDate, "dd 'de' MMMM 'de' yyyy", { locale: ptBR })}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="start">
            <Calendar
              mode="single"
              selected={firstDueDate}
              onSelect={(date) => date && setFirstDueDate(date)}
              locale={ptBR}
              className="pointer-events-auto"
            />
          </PopoverContent>
        </Popover>
      </div>
      
      <div className="space-y-2">
        <Label>Prévia das parcelas</Label>
        <div className="rounded-lg border divide-y max-h-48 overflow-y-auto">
          {installmentsList.map((inst) => (
            <div key={inst.number} className="flex justify-between items-center px-4 py-3 text-sm">
              <span className="font-medium">{inst.number}ª parcela</span>
              <div className="text-right">
                <p className="font-semibold">{formatCurrency(inst.value)}</p>
                <p className="text-xs text-muted-foreground">
                  Vence em {format(inst.dueDate, "dd/MM/yyyy")}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
  
  const FooterButtons = (
    <>
      <Button variant="outline" onClick={() => onOpenChange(false)}>
        Cancelar
      </Button>
      <Button 
        onClick={handleCreate}
        disabled={createBulkPayments.isPending}
      >
        {createBulkPayments.isPending ? 'Criando...' : `Criar ${installments} Parcelas`}
      </Button>
    </>
  )
  
  if (isMobile) {
    return (
      <Drawer open={open} onOpenChange={onOpenChange}>
        <DrawerContent className="max-h-[90vh]">
          <DrawerHeader>
            <DrawerTitle>Dividir em Parcelas</DrawerTitle>
            <DrawerDescription>{projectName}</DrawerDescription>
          </DrawerHeader>
          <div className="overflow-y-auto px-4 pb-4">
            {Content}
          </div>
          <DrawerFooter className="flex-row gap-2">
            {FooterButtons}
          </DrawerFooter>
        </DrawerContent>
      </Drawer>
    )
  }
  
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Dividir em Parcelas</DialogTitle>
          <DialogDescription>{projectName}</DialogDescription>
        </DialogHeader>
        {Content}
        <DialogFooter>
          {FooterButtons}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
